import React, { useState, useEffect, useMemo  } from "react";
import {
  Container,
  Row,
  Col,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Button,
  Input,
  Label,
  FormGroup,
  Card,
  CardBody,
  CardTitle,
  Pagination,
  PaginationItem,
  PaginationLink,
  Spinner
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { getChunkData } from "../../../helpers/fakebackend_helper";


/**
 * Recursive editor component:
 * - Renders primitive values as inputs/textareas
 * - Renders objects and arrays recursively
 * - Calls onChange(path, newValue) where path is array of keys/indexes
 */
function RecursiveEditor({ value, path = [], onChange, name, keys, readOnly = false }) {
  const { t } = useTranslation();
  const isObject = value && typeof value === "object" && !Array.isArray(value);
  const isArray = Array.isArray(value);

  const handlePrimitiveChange = (newVal) => {
    onChange(path, newVal);
  };

  if (isObject) {
    // Pick only allowed keys if `keys` is passed
    // const objectKeys = keys ? keys.filter((k) => Object.prototype.hasOwnProperty.call(value, k)) : Object.keys(value);
    const objectKeys = keys ? Object.keys(value).filter((k) => !keys.includes(k)) : Object.keys(value);

    return (
      <div style={{ borderLeft: "2px solid #eee", paddingLeft: 12, marginBottom: 8 }}>
        {name && <div style={{ fontWeight: 600, marginBottom: 6 }}>{t(name)}</div>}
        {objectKeys.map((key) => (
          <RecursiveEditor
            key={key}
            name={key}
            value={value[key]}
            path={[...path, key]}
            onChange={onChange}
            readOnly={readOnly}
          />
        ))}
      </div>
    );
  } else if (isArray) {
    return (
      <div style={{ borderLeft: "2px dashed #eee", paddingLeft: 12, marginBottom: 8 }}>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>
          {t(name)} ({t('length')}: {value.length})
        </div>
        {value.map((item, idx) => (
          <div key={idx} style={{ marginBottom: 8 }}>
            <RecursiveEditor
              name={`[${idx}]`}
              value={item}
              path={[...path, idx]}
              onChange={onChange}
              keys={keys} // pass down whitelist for nested objects
              readOnly={readOnly}
            />
          </div>
        ))}
      </div>
    );
  } else {
    if (name === "validation_needs_review") {
      return (
        <>
          <FormGroup className="d-flex gap-1" style={{ marginBottom: 8 }}>
            <Label style={{ fontWeight: 600, fontSize: 14 }}>{t(name)}</Label>
            <Input
              type="checkbox"
              checked={value == true}
              onChange={(e) => handlePrimitiveChange(e.target.checked)}
              readOnly={readOnly}
            />
          </FormGroup>
        </>
      );
    }

    // primitive value
    const asString = value === null ? "" : String(value);
    const useTextarea = asString.length > 60 || asString.includes("\n") || /[اأإآ]/.test(asString);

    return (
      <FormGroup style={{ marginBottom: 8 }}>
        <Label style={{ fontWeight: 600, fontSize: 14 }}>{t(name)}</Label>
        {useTextarea ? (
          <Input
            type="textarea"
            value={asString}
            rows={5}
            onChange={(e) => handlePrimitiveChange(e.target.value)}
            readOnly={readOnly}
          />
        ) : (
          <Input
            type="text"
            value={asString}
            onChange={(e) => handlePrimitiveChange(e.target.value)}
            readOnly={readOnly}
          />
        )}
      </FormGroup>
    );
  }
}


/**
 * Helper: update nested value immutably given a path (array of keys/indexes)
 */
function updateAtPath(obj, path, newValue) {
  if (path.length === 0) {
    return newValue;
  }
  const [head, ...rest] = path;
  let clone;
  if (Array.isArray(obj)) clone = [...obj];
  else if (obj && typeof obj === "object") clone = { ...obj };
  else {
    // if path deeper but current is primitive, create object/array depending on next key
    clone = typeof head === "number" ? [] : {};
  }

  clone[head] = updateAtPath(clone[head], rest, newValue);
  return clone;
}

/**
 * Main app
 */
const DocumentChunk = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  let { state = {} } = useLocation();
  
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("document");
  const [articleIndex, setArticleIndex] = useState(0);

  // Article pagination control
  const [openAccordion, setOpenAccordion] = useState(["1"]);

  const toggleAccordion = (id) => {
    if (openAccordion.includes(id)) {
      setOpenAccordion(openAccordion.filter((x) => x !== id));
    } else {
      setOpenAccordion([...openAccordion, id]); // multiple open allowed
    }
  };

  const onChangeValue = (path, newVal) => {
    // newVal comes as string for primitives; attempt to coerce booleans/numbers/null if appropriate
    const parsedVal = attemptParsePrimitive(newVal);
    const updated = updateAtPath(data, [articleIndex, ...path], parsedVal);
    setData(updated);
  };

  function attemptParsePrimitive(v) {
    // keep strings by default; but if user typed 'true'/'false'/'null'/'123' we might parse
    if (v === null) return null;
    if (typeof v !== "string") return v;
    const trimmed = v.trim();
    if (trimmed === "") return "";
    if (trimmed === "null") return null;
    if (trimmed === "true") return true;
    if (trimmed === "false") return false;
    // numeric?
    if (!isNaN(Number(trimmed)) && trimmed.length < 16) {
      return Number(trimmed);
    }
    return v;
  }

  // helper to update top-level fields (document_type etc.)
  const updateArticleField = (fieldPath, newValue) => {
    const updated = updateAtPath(data, [articleIndex, ...fieldPath], newValue);
    setData(updated);
  };


  // Article pagination helpers
  const articleCount = data.length;
  const selectedArticle = data[articleIndex] || {};


  const gotoArticle = (idx) => {
    const newIdx = Math.max(0, Math.min(articleCount - 1, idx));
    setArticleIndex(newIdx);
  };

  const articlePagination = useMemo(() => {
    const total = articleCount;       // total pages
    const current = articleIndex;     // current page index (0-based)
    const maxVisible = 7;             // max pages to display in window
    const pages = [];

    if (total <= 1) return [];
    // if (total <= 1) return null;

    // Always include first page
    pages.push(0);

    // Case 1: if total pages ≤ maxVisible → show all
    if (total <= maxVisible) {
      for (let i = 1; i < total; i++) {
        pages.push(i);
      }
      return pages;
    }

    // Define sliding window
    let start = Math.max(1, current - 2);
    let end = Math.min(total - 2, current + 2);

    // Ensure window always has (maxVisible - 2) slots (excluding first & last)
    const windowSize = maxVisible - 2;
    if (end - start + 1 < windowSize) {
      if (current <= Math.floor(total / 2)) {
        end = Math.min(total - 2, start + windowSize - 2);
      } else {
        start = Math.max(1, end - windowSize + 1);
      }
    }

    // Left ellipsis
    if (start > 1) {
      pages.push("ellipsis-left");
    }

    // Middle pages
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    // Right ellipsis
    if (end < total - 2) {
      pages.push("ellipsis-right");
    }

    // Always include last page
    pages.push(total - 1);

    return pages;
  }, [articleCount, articleIndex]);


  useEffect(() => {
    const fetchJson = async () => {
      try {
        if (!state.JsonURL) return;
        const response = await getChunkData({ JsonURL: state.JsonURL })

        const jsonData = await response.data;
        setData(jsonData);
      } catch (err) {
        console.error("Error fetching JSON:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchJson();
  }, [state.JsonURL]);

  return (
    <div className="page-content">
      <Container fluid>
        <Row>
          <Col>
            <Card>
              <CardBody>
        
                <CardTitle tag="h4">{t('Article Editor')}</CardTitle>
                <Row className="align-items-center">
                    <Col md="6">
                        <div className="d-flex gap-2 justify-content-start">
                        <Button color="secondary" onClick={() => navigate(-1)}>
                            <i className={`${i18n.language === 'en' ? "ri-arrow-left-line" : "ri-arrow-right-line"}`} />
                        </Button>
                        </div>
                    </Col>
                </Row>

                <Nav tabs style={{ marginTop: 16 }}>
                    <NavItem>
                        <NavLink
                        className={activeTab === "document" ? "active" : ""}
                        onClick={() => setActiveTab("document")}
                        style={{ cursor: "pointer" }}
                        >
                        {t('Document Info')}
                        </NavLink>
                    </NavItem>
                    {/* <NavItem>
                        <NavLink
                        className={activeTab === "article" ? "active" : ""}
                        onClick={() => setActiveTab("article")}
                        style={{ cursor: "pointer" }}
                        >
                        {t('Article')}
                        </NavLink>
                    </NavItem>
                    <NavItem>
                        <NavLink
                        className={activeTab === "forms_flat" ? "active" : ""}
                        onClick={() => setActiveTab("forms_flat")}
                        style={{ cursor: "pointer" }}
                        >
                        {t('forms_flat')}
                        </NavLink>
                    </NavItem>
                    <NavItem>
                        <NavLink
                        className={activeTab === "annexes" ? "active" : ""}
                        onClick={() => setActiveTab("annexes")}
                        style={{ cursor: "pointer" }}
                        >
                        {t('annexes')}
                        </NavLink>
                    </NavItem> */}
                </Nav>

                <TabContent activeTab={activeTab} style={{ marginTop: 12 }}>
                    <TabPane tabId="document">
                        <Row className="justify-content-center mt-3" style={{ height: "80vh" }}>
                        { isLoading ? 
                        <Col md="6">
                            <div className="d-flex justify-content-center align-items-center mt-5">
                                <Spinner
                                    style={{ height: "3rem", width: "3rem" }}
                                    className="me-2"
                                >
                                    Loading...
                                </Spinner>
                            </div> 
                        </Col> :
                        <Col md={6}
                            style={{
                            height: "100%",
                            overflowY: "auto", // vertical scrollbar for PDF
                            }}>
                            <div className="d-flex justify-content-between align-items-center">
                              <div>
                                <strong>{t('Document Info')}</strong>{" "}
                                <small className="text-muted">({articleCount})</small>
                              </div>
                            </div>

                            <Card style={{ marginTop: 12 }}>
                              <CardBody>
                                <RecursiveEditor
                                  value={data[0] || {}}
                                  onChange={onChangeValue} 
                                  readOnly={true}
                                  />
                              </CardBody>
                            </Card>

                        </Col>
                        }
                        <Col md="6"
                            style={{
                            height: "100%",
                            overflowY: "auto",
                            overflowX: "hidden",
                            }}>
                            <iframe
                            src={state.FileURL || ''}
                            width="100%"
                            height="95%"
                            title="PDF Viewer"
                            style={{ minHeight: "600px" }}
                            ></iframe>
                        </Col>
                        </Row>
                    </TabPane>
                    <TabPane tabId="article">
                        <Row className="justify-content-center mt-3" style={{ height: "80vh" }}>
                        { isLoading ? 
                        <Col md="6">
                            <div className="d-flex justify-content-center align-items-center mt-5">
                                <Spinner
                                    style={{ height: "3rem", width: "3rem" }}
                                    className="me-2"
                                >
                                    Loading...
                                </Spinner>
                            </div> 
                        </Col> :
                        <Col md={6}
                            style={{
                            height: "100%",
                            overflowY: "auto", // vertical scrollbar for PDF
                            }}>
                            <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <strong>{t('Total Articles')}</strong>{" "}
                                <small className="text-muted">({articleCount})</small>
                            </div>
                            </div>

                            {articleCount === 0 ? (
                            <div style={{ marginTop: 12 }}>{t('NoArticlesAvailable')}</div>
                            ) : (
                            <>
                                <Card style={{ marginTop: 12 }}>
                                <CardBody>
                                    <RecursiveEditor
                                    name={`metadata`}
                                    value={selectedArticle}
                                    path={[articleIndex]}
                                    onChange={onChangeValue} 
                                    keys={['forms_flat', 'annexes']}
                                    readOnly={true}
                                    />
                                </CardBody>
                                </Card>

                                <div className="d-flex justify-content-between align-items-center" style={{ marginTop: 12 }}>
                                <Pagination>
                                    <PaginationItem disabled={articleIndex === 0}>
                                    <PaginationLink previous onClick={() => gotoArticle(articleIndex - 1)} />
                                    </PaginationItem>

                                    {articlePagination.map((p, idx) =>
                                    p === "ellipsis-left" || p === "ellipsis-right" ? (
                                        <PaginationItem key={idx} disabled>
                                        <PaginationLink>...</PaginationLink>
                                        </PaginationItem>
                                    ) : (
                                        <PaginationItem active={p === articleIndex} key={idx}>
                                        <PaginationLink onClick={() => gotoArticle(p)}>
                                            {p + 1}
                                        </PaginationLink>
                                        </PaginationItem>
                                    )
                                    )}

                                    <PaginationItem disabled={articleIndex === articleCount - 1}>
                                    <PaginationLink next onClick={() => gotoArticle(articleIndex + 1)} />
                                    </PaginationItem>
                                </Pagination>
                                </div>
                            </>
                            )}
                        </Col>
                        }
                        <Col md="6"
                            style={{
                            height: "100%",
                            overflowY: "auto",
                            overflowX: "hidden",
                            }}>
                            <iframe
                            src={state.FileURL || ''}
                            width="100%"
                            height="95%"
                            title="PDF Viewer"
                            style={{ minHeight: "600px" }}
                            ></iframe>
                        </Col>
                        </Row>
                    </TabPane>
                    <TabPane tabId="forms_flat">
                        <Row className="justify-content-center mt-3" style={{ height: "80vh" }}>
                            <Col md={6}
                                style={{
                                height: "100%",
                                overflowY: "auto", // vertical scrollbar for PDF
                            }}>
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <strong>{t('forms_flat')}</strong>{" "}
                                    </div>
                                </div>

                                <Card style={{ marginTop: 12 }}>
                                    <CardBody>
                                        <RecursiveEditor
                                        name={`forms_flat`}
                                        value={selectedArticle.forms_flat}
                                        path={[articleIndex]}
                                        onChange={onChangeValue} 
                                        readOnly={true}
                                        />
                                    </CardBody>
                                </Card>
                            </Col>
                            <Col md="6"
                                style={{
                                height: "100%",
                                overflowY: "auto",
                                overflowX: "hidden",
                                }}>
                                <iframe
                                    src={state.FileURL || ''}
                                    width="100%"
                                    height="95%"
                                    title="PDF Viewer"
                                    style={{ minHeight: "600px" }}
                                ></iframe>
                            </Col>
                        </Row>
                    </TabPane>
                    <TabPane tabId="annexes">
                        <Row className="justify-content-center mt-3" style={{ height: "80vh" }}>
                            <Col md={6}
                                style={{
                                height: "100%",
                                overflowY: "auto", // vertical scrollbar for PDF
                                }}>
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <strong>{t('annexes')}</strong>{" "}
                                    </div>
                                </div>

                                <Card style={{ marginTop: 12 }}>
                                    <CardBody>
                                        <RecursiveEditor
                                        name={`annexes`}
                                        value={selectedArticle.annexes}
                                        path={[articleIndex]}
                                        onChange={onChangeValue} 
                                        readOnly={true}
                                        />
                                    </CardBody>
                                </Card>

                            </Col>
                            <Col md="6"
                                style={{
                                height: "100%",
                                overflowY: "auto",
                                overflowX: "hidden",
                                }}>
                                <iframe
                                src={state.FileURL || ''}
                                width="100%"
                                height="95%"
                                title="PDF Viewer"
                                style={{ minHeight: "600px" }}
                                ></iframe>
                            </Col>
                        </Row>
                    </TabPane>
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row> 
      </Container>
    </div>
  );
}

export default DocumentChunk;